import React from 'react';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import TransactionsService from 'services/transactions.service';
import { Dialog, DialogContent, Box, Typography, IconButton, Button, Stack, Alert, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadIcon from '@mui/icons-material/Upload';
import DescriptionIcon from '@mui/icons-material/Description';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DownloadIcon from '@mui/icons-material/Download';

interface EmitirBoletoLoteModalProps {
  open: boolean;
  onClose: () => void;
}

const BoletoLote = ({ open, onClose }: EmitirBoletoLoteModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const [polling, setPolling] = React.useState(false);
  const [processingData, setProcessingData] = React.useState<any>(null);
  const transactionsService = new TransactionsService();

  const pollStatus = React.useCallback(
    async (id: string) => {
      try {
        const statusData = await transactionsService.getProcessingStatus(id);

        if (statusData.status === 'FINISH' || statusData.status === 'ERROR') {
          setPolling(false);
          setProcessingData(statusData);
          if (statusData.status === 'FINISH') {
            enqueueSnackbar('Processamento concluído com sucesso!', { variant: 'success' });
          } else {
            enqueueSnackbar('Erro no processamento do arquivo.', { variant: 'error' });
          }
        } else {
          setTimeout(() => pollStatus(id), 30000);
        }
      } catch (error) {
        setTimeout(() => pollStatus(id), 30000);
      }
    },
    [transactionsService, enqueueSnackbar]
  );

  const onDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      if (!file.name.endsWith('.xlsx')) {
        enqueueSnackbar('Formato de arquivo inválido. Por favor, envie um arquivo .xlsx', { variant: 'warning' });
        return;
      }

      setLoading(true);
      setProcessingData(null);
      try {
        const response = await transactionsService.createMultipleBankSlips(file);
        setLoading(false);
        setPolling(true);
        pollStatus(response.processingId);
      } catch (error: any) {
        setLoading(false);
        enqueueSnackbar(error?.response?.data?.detail || 'Erro ao enviar arquivo.', { variant: 'error' });
      }
    },
    [onClose, enqueueSnackbar, pollStatus]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    maxFiles: 1,
    multiple: false,
    disabled: loading || polling,
  });

  const handleClose = () => {
    if (polling) {
      enqueueSnackbar('Aguarde o processamento finalizar para fechar.', { variant: 'warning' });
      return;
    }
    onClose();
    setProcessingData(null);
  };

  const dropzoneText = React.useMemo(() => {
    if (loading) return 'Enviando arquivo...';
    if (polling) return 'Processando... Isso pode levar alguns minutos.';
    if (isDragActive) return 'Solte o arquivo aqui';
    return 'Selecione a planilha .xlsx ou arraste e solte aqui';
  }, [loading, polling, isDragActive]);

  return (
    <Dialog
      open={open}
      onClose={polling ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
        },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="center" px={1} py={1} position="relative">
        <Box display="flex" alignItems="center" gap={1} justifyContent="center" width="100%">
          <DescriptionIcon color="primary" />
          <Typography fontWeight={600} fontSize={18}>
            Emitir boleto em lote (Planilha)
          </Typography>
        </Box>

        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
          disabled={polling}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Typography mb={2} mt={-2} textAlign="center">
          Importe uma planilha excel .xlsx para a emissão em lote
        </Typography>

        <Box bgcolor="grey.100" borderRadius={2} p={3} mb={3}>
          <Typography fontWeight={600} mb={1}>
            Instruções:
          </Typography>

          <Box component="ul" pl={3} mb={3}>
            <li>Baixe a planilha modelo e preencha com os dados dos boletos a serem emitidos;</li>
            <li>Não altere o cabeçalho (nome das colunas);</li>
            <li>Após a importação, se houver erros de preenchimento você será avisado;</li>
            <li>Caso tenha dúvida no preenchimento, assista o tutorial.</li>
          </Box>

          <Stack direction="row" spacing={2} width="100%">
            <Button
              variant="contained"
              fullWidth
              startIcon={<DownloadOutlinedIcon />}
              onClick={() => window.open('https://torqi-public.s3.us-east-1.amazonaws.com/exemplo_planilha.xlsx', '_blank')}
              sx={{
                borderRadius: 999,
                px: 3,
                textTransform: 'none',
                fontWeight: 600,
                flex: 1,
              }}
            >
              Baixar Planilha Modelo
            </Button>

            <Button
              variant="contained"
              fullWidth
              startIcon={<PlayCircleOutlineIcon />}
              sx={{
                borderRadius: 999,
                px: 3,
                textTransform: 'none',
                fontWeight: 600,
                flex: 1,
              }}
              disabled
            >
              Assistir Tutorial Preenchimento (Em breve)
            </Button>
          </Stack>
        </Box>

        {processingData ? (
          <Box mt={3}>
            {processingData.status === 'FINISH' ? (
              <Box display="flex" flexDirection="column" gap={3}>
                <Typography fontWeight={600} sx={{ mt: 3 }} textAlign="center">
                  Resultado do processamento:
                </Typography>

                {(processingData.fail || 0) > 0 && (processingData.success || 0) > 0 && (
                  <Alert
                    severity="warning"
                    sx={{ width: 'fit-content', color: '#000', mx: 'auto', background: 'none', '& .MuiAlert-icon': { color: '#A34900' } }}
                  >
                    Arquivo aceito parcialmente
                  </Alert>
                )}

                {(processingData.success || 0) > 0 && (
                  <Box border="1px solid" borderColor="#007C40" borderRadius={2} bgcolor="#EFFBF5" px={3} py={2}>
                    <Typography fontWeight={700} color="#007C40" mb={1}>
                      Itens aceitos: {processingData.success || 0}
                    </Typography>

                    <Stack component="ul" spacing={1} pl={2} m={0}>
                      <Typography component="li">
                        As solicitações foram enviadas para a instituição financeira para emissão de boleto;
                      </Typography>

                      <Typography component="li">
                        O tempo de retorno é de até <strong>1</strong> minutos;
                      </Typography>

                      <Typography component="li">
                        Verifique na tabela de Boletos Emitidos na tela resumo se eventualmente houve falha na emissão de algum boleto por
                        parte da instituição financeira.
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {(processingData.fail || 0) > 0 && (
                  <Box
                    border="1px solid"
                    borderColor="#C8004C"
                    borderRadius={2}
                    bgcolor="#FDECEE"
                    px={3}
                    py={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={3}
                  >
                    <Box>
                      <Typography fontWeight={700} color="#C8004C" mb={1}>
                        Itens recusados: {processingData.fail || 0}
                      </Typography>
                      <Stack component="ul" spacing={1} pl={2} m={0}>
                        <Typography component="li">Clique no botão ao lado para baixar as linhas recusadas para ajuste.</Typography>
                        <Typography component="li">Após o ajuste a planilha deve ser novamente submetida.</Typography>
                      </Stack>
                    </Box>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      disabled={!processingData.errorSheetUrl}
                      onClick={() => {
                        if (processingData.errorSheetUrl) {
                          window.open(processingData.errorSheetUrl, '_blank');
                        }
                      }}
                      sx={{
                        borderRadius: 999,
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        px: 3,
                      }}
                    >
                      Baixar Planilha com erros
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />}>
                Erro no processamento: {processingData.error || 'Erro desconhecido'}
              </Alert>
            )}
            <Box display="flex" justifyContent="center" mt={2}>
              <Button onClick={() => setProcessingData(null)} variant="outlined">
                Enviar novo arquivo
              </Button>
            </Box>
          </Box>
        ) : (
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Box
              border="2px dashed"
              borderColor={isDragActive ? 'primary.main' : 'primary.light'}
              borderRadius={2}
              height={180}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              bgcolor={isDragActive ? 'action.hover' : 'transparent'}
              sx={{ cursor: loading || polling ? 'default' : 'pointer', opacity: loading || polling ? 0.6 : 1 }}
            >
              <Box
                width={56}
                height={56}
                borderRadius="50%"
                bgcolor="action.hover"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={2}
              >
                {loading || polling ? <CircularProgress size={30} /> : <UploadIcon color="primary" fontSize="large" />}
              </Box>

              <Typography fontWeight={600}>{dropzoneText}</Typography>

              {!loading && !polling && (
                <Typography color="text.secondary" fontSize={14}>
                  Clique aqui para selecionar do seu computador
                </Typography>
              )}
            </Box>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BoletoLote;
